import React from "react";
import PackageFieldInput from "../../common/PickupComponents/PackageFieldInput";
import StandaloneFieldInput from "../../common/PickupComponents/StandaloneFieldInput";
import { isPackageDetailsRowField } from "../../common/packagingConfig";

const visibleFieldNames = (fieldOrder, fieldMap) =>
  fieldOrder.filter((name) => Boolean(fieldMap?.[name]?.visible));

const toStandaloneItem = (fieldName, fieldConfig) => ({
  key: fieldName,
  fieldName,
  fieldConfig,
});

const buildPackageDetailsRowFieldNames = ({ fieldOrder, fieldMap, groupTarget }) =>
  visibleFieldNames(fieldOrder, fieldMap).filter((name) =>
    isPackageDetailsRowField(fieldMap[name])
  );

const toRowItem = (fieldName, fieldConfig, packageIndex) => ({
  key: `${fieldName}_${packageIndex}`,
  fieldName,
  fieldConfig,
  packageIndex,
});

const toRowBlockItems = ({ fieldNames, fieldMap, packagingItems }) =>
  packagingItems.flatMap((_, packageIndex) =>
    fieldNames.map((fieldName) =>
      toRowItem(fieldName, fieldMap[fieldName], packageIndex)
    )
  );

const SEGMENT_TYPES = {
  STANDALONE: "standalone",
  ROW_BLOCK: "rowBlock",
};

const toStandaloneSegment = (fieldName) => ({
  type: SEGMENT_TYPES.STANDALONE,
  fieldName,
});

const toRowBlockSegment = (fieldNames) => ({
  type: SEGMENT_TYPES.ROW_BLOCK,
  fieldNames,
});

const buildRenderSegments = ({ fieldOrder, fieldMap, groupTarget }) => {
  const visibleFields = visibleFieldNames(fieldOrder, fieldMap);
  const targetGroupFieldNames = visibleFields.filter((fieldName) => {
    const cfg = fieldMap[fieldName];
    return cfg.group && groupTarget && cfg.group === groupTarget;
  });
  const targetGroupFieldNameSet = new Set(targetGroupFieldNames);

  const segments = [];
  let nonGroupedRowFieldCluster = [];
  let targetGroupInserted = false;

  const flushNonGroupedRowFieldCluster = () => {
    if (nonGroupedRowFieldCluster.length) {
      segments.push(toRowBlockSegment(nonGroupedRowFieldCluster));
      nonGroupedRowFieldCluster = [];
    }
  };

  visibleFields.forEach((fieldName) => {
    const cfg = fieldMap[fieldName];
    const isRowField = isPackageDetailsRowField(cfg);
    const isTargetGroupField = targetGroupFieldNameSet.has(fieldName);

    if (isTargetGroupField) {
      flushNonGroupedRowFieldCluster();
      if (!targetGroupInserted) {
        segments.push(toRowBlockSegment(targetGroupFieldNames));
        targetGroupInserted = true;
      }
      return;
    }

    if (isRowField) {
      if (!cfg.group) {
        nonGroupedRowFieldCluster.push(fieldName);
      }
      return;
    }

    flushNonGroupedRowFieldCluster();
    if (!cfg.group) {
      segments.push(toStandaloneSegment(fieldName));
    }
  });

  flushNonGroupedRowFieldCluster();
  return segments;
};

/**
 * Render items in field order. The configured add-item target group is collapsed
 * into one repeatable row block at its first logical position; each add-item click
 * adds one full block without disturbing surrounding standalone fields.
 */
export const buildPackageDetailsRenderItems = ({
  fieldOrder,
  packagingConfig,
  packagingItems,
}) => {
  const fieldMap = packagingConfig.fieldMap;
  const groupTarget = packagingConfig?.miscellaneous?.addItem?.groupTarget;

  const packageDetailsRowFieldNames = buildPackageDetailsRowFieldNames({
    fieldOrder,
    fieldMap,
    groupTarget,
  });
  if (!packageDetailsRowFieldNames.length) {
    return visibleFieldNames(fieldOrder, fieldMap)
      .filter((fieldName) => !fieldMap[fieldName].group)
      .map((fieldName) => toStandaloneItem(fieldName, fieldMap[fieldName]));
  }

  const segments = buildRenderSegments({
    fieldOrder,
    fieldMap,
    groupTarget,
  });

  return segments.flatMap((segment) => {
    if (segment.type === SEGMENT_TYPES.STANDALONE) {
      return toStandaloneItem(segment.fieldName, fieldMap[segment.fieldName]);
    }
    return toRowBlockItems({
      fieldNames: segment.fieldNames,
      fieldMap,
      packagingItems,
    });
  });
};

export const renderPackageDetailsField = ({
  item,
  groupTarget,
  handleChange,
  errorInfo,
  editInfo,
  packageTypeList,
}) =>
  isPackageDetailsRowField(item.fieldConfig) ? (
    <PackageFieldInput
      fieldName={item.fieldName}
      fieldConfig={item.fieldConfig}
      handleChange={handleChange}
      errorInfo={errorInfo}
      editInfo={editInfo}
      packageTypeList={packageTypeList}
      packageIndex={item.packageIndex}
    />
  ) : (
    <StandaloneFieldInput
      fieldName={item.fieldName}
      fieldConfig={item.fieldConfig}
      handleChange={handleChange}
      editInfo={editInfo}
      errorInfo={errorInfo}
    />
  );
